import { useCallback, useEffect, useState } from "react";
import { Ban, Loader2, RotateCcw, Wallet } from "lucide-react";
import adminApi from "../lib/adminApi";
import api, { errorText } from "../lib/api";
import { inAdminArea } from "../lib/adminBase";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { CreditBackfill } from "./CreditBackfill";
import { usd } from "../lib/money";

const money = (n) => (n === null || n === undefined ? "—" : usd(n));
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const MAX = 1000;
const MANAGES = { admin: ["customer", "partner", "staff"], owner: ["customer", "partner", "staff", "admin", "owner"] };

// Same rule as the server (POST /api/accounts/{id}/allowance): signed in here as admin / owner, never your own row,
// and only the owner changes admin and owner accounts. Inside Shopify admin the table is view only.
export const canChange = (me, u) => inAdminArea() && !!me && u.user_id !== me.id && (MANAGES[me.role] || []).includes(u.role || "customer");

function CreditList({ credits, userId }) {
  if (!credits?.length) return null;
  return (
    <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground font-mono2" data-testid={`user-spend-credits-${userId}`}>
      {credits.map((c, i) => (
        <li key={`${c.order_name}-${c.duck_id}-${i}`} className={c.state === "reversed" ? "line-through" : ""}>
          {c.order_name} · {c.duck_name || c.duck_id} · +{money(c.amount_usd)}
          {c.state === "reversed" ? ` · reversed (${c.reversed_reason})` : c.state === "capped" ? " · maximum reached" : ""} · {when(c.granted_at || c.created_at)}
        </li>
      ))}
    </ul>
  );
}

const stateOf = (u) => (u.blocked ? "blocked" : u.over ? "over the cap" : u.reached ? "limit reached"
  : u.owner_allowance ? "owner allowance · this month" : "within allowance");
const toneOf = (u) => (u.blocked || u.over ? "text-rose-700" : u.reached ? "text-amber-800" : u.owner_allowance ? "text-sky-700" : "text-emerald-700");

function Figures({ u }) {
  const own = u.allowance_usd !== null && u.allowance_usd !== undefined && !u.allowance_unlimited;
  const month = u.window === "month";
  return (
    <p className="mt-1 text-xs text-muted-foreground font-mono2" data-testid={`user-spend-figures-${u.user_id}`}>
      {`base ${money(u.base_cap_usd)}${own ? " (own allowance)" : u.owner_allowance ? " (owner allowance)" : ""}${month ? " a month" : ""}`}
      {` + orders ${money(u.credits_usd)} = allowance ${money(u.cap_usd)} · used ${money(u.used_usd)}${month ? " this month" : ""} · remaining ${money(u.remaining_usd)}`}
      {u.pct !== null && u.pct !== undefined ? ` · ${u.pct}%` : ""}
      {u.allowance_unlimited ? " · legacy unlimited, read as the owner allowance" : ""}
      {u.forgiven_usd ? ` · ${money(u.forgiven_usd)} forgiven (lifetime recorded ${money(u.spent_usd)})` : ""}
      {" · "}{u.calls} paid call{u.calls === 1 ? "" : "s"} · last {when(u.last_at)}{u.last_phase ? ` (${u.last_phase})` : ""}
    </p>
  );
}

const inputCls = "rounded-full border border-border bg-card px-3 h-9 text-sm outline-none focus:ring-2 ring-accent/40";

function Controls({ u, onAdjust, busy }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const ok = !busy && reason.trim().length >= 3;
  const amountOk = amount !== "" && Number(amount) >= 0 && Number(amount) <= MAX;
  const send = (body) => onAdjust(u.user_id, { ...body, reason: reason.trim() }).then((done) => done && (setAmount(""), setReason("")));
  const btn = (id, label, Icon, body, enabled = ok) => (
    <Button data-testid={`user-spend-${id}-${u.user_id}`} size="sm" variant={id === "raise" ? "default" : "outline"}
      className={`rounded-full h-9 gap-1.5 ${id === "raise" ? "font-bold" : ""}`} disabled={!enabled} onClick={() => send(body)}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {label}
    </Button>
  );
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <input data-testid={`user-spend-amount-${u.user_id}`} value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
        placeholder={`new allowance $0-${MAX}`} aria-label="New allowance in USD" className={`w-44 ${inputCls}`} />
      <input data-testid={`user-spend-note-${u.user_id}`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200}
        placeholder="reason (required, audited)" aria-label="Reason for the change" className={`flex-1 min-w-[160px] ${inputCls}`} />
      {btn("raise", "Set allowance", Wallet, { allowance_usd: Number(amount) }, ok && amountOk)}
      {btn("block", "Block ($0)", Ban, { allowance_usd: 0 })}
      {btn("reset", "Reset used to $0", RotateCcw, { reset: true })}
      {(u.allowance_usd !== null && u.allowance_usd !== undefined) || u.allowance_unlimited
        ? btn("clear", "Back to default", null, { clear_allowance: true }) : null}
    </div>
  );
}

function Row({ u, onAdjust, busy, editable }) {
  return (
    <li data-testid={`user-spend-row-${u.user_id}`} className="rounded-xl border border-border bg-background px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-semibold truncate" data-testid={`user-spend-email-${u.user_id}`}>{u.email || u.user_id}</span>
        <span className={`font-mono2 text-xs font-bold uppercase tracking-widest ${toneOf(u)}`} data-testid={`user-spend-state-${u.user_id}`}>
          {stateOf(u)}
        </span>
      </div>
      <Figures u={u} />
      <CreditList credits={u.credits} userId={u.user_id} />
      {editable && <Controls u={u} onAdjust={onAdjust} busy={busy} />}
      {(u.adjustments || []).length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground font-mono2" data-testid={`user-spend-adjustments-${u.user_id}`}>
          {u.adjustments.map((a, i) => (
            <li key={`${a.at}-${i}`}>
              {when(a.at)} · {a.by}
              {a.allowance_usd !== undefined ? ` · allowance ${a.allowance_usd === null ? "default" : a.allowance_usd === "unlimited" ? "unlimited (legacy)" : money(a.allowance_usd)}` : ""}
              {a.reset_from_usd !== undefined ? ` · reset from ${money(a.reset_from_usd)}` : ""}
              {a.note ? ` · ${a.note}` : ""}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

const creditSummary = (s) =>
  !s ? "…" : s.order_credit_usd > 0
    ? `+${money(s.order_credit_usd)} per paid duck line (${s.on === "shipped" ? "once shipped" : "when paid"}), max ${s.max_usd > 0 ? money(s.max_usd) : "none"}`
    : "order top-up OFF";

// Staff re-reads of a customer's duck: staff spend, never on the customer's allowance.
function StaffSpendList({ staff }) {
  if (!staff?.length) return null;
  return (
    <div className="mt-4" data-testid="user-spend-staff">
      <p className="text-sm font-semibold">Staff spend (staff colour re-reads, not on any maker's allowance)</p>
      <ul className="mt-1 space-y-1 text-[11px] text-muted-foreground font-mono2">
        {staff.map((s) => (
          <li key={s.email} data-testid={`user-spend-staff-${s.email}`}>
            <span className="font-semibold text-foreground">{s.email}</span> · {money(s.spent_usd)} · {s.calls} staff re-read{s.calls === 1 ? "" : "s"} · last {when(s.last_at)}
            {(s.recent || []).length > 0 && ` · ducks ${s.recent.map((r) => `${String(r.creation_id || "").slice(0, 8)} ${money(r.usd)}`).join(", ")}`}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Summary({ data }) {
  return (
    <>
      <span className="font-mono2 text-xs text-muted-foreground" data-testid="user-spend-default">
        base {data ? money(data.cap_default_usd) : "…"} · lifetime per account · owner + exempt {data ? money(data.owner_allowance_usd) : "…"} a month ·
        max {money(data?.max_allowance_usd ?? MAX)} · {data?.exempt_email_count || 0} exempt address{(data?.exempt_email_count || 0) === 1 ? "" : "es"} ·{" "}
        <span data-testid="user-spend-credit-settings">{creditSummary(data?.credit_settings)}</span>
      </span>
      {data && (
        <p data-testid="user-spend-legacy-unlimited" className="mt-1 w-full text-xs text-muted-foreground font-mono2">
          Legacy "unlimited" rows: {data.legacy_unlimited || 0} (read as the owner allowance; their data is unchanged)
        </p>
      )}
    </>
  );
}

const viewOnlyText = () => (inAdminArea() ? "View only: admins change allowances (owner: also admin accounts)."
  : "View only inside Shopify admin: change allowances in the Duckify admin, signed in as an admin with 2-step.");

// Every maker's generation spend against their allowance (base + print-order credits), the unmatched orders and the
// dry-run backfill. Changes go through the Accounts allowance route only: admin / owner, $0-1000, a reason, audited.
export const UserSpendTable = () => {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/user-spend");
      setData(data);
      setError("");
    } catch {
      setError("Could not load the per-user spend table.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const adjust = async (userId, body) => {
    setBusy(true);
    try {
      await api.post(`/accounts/${userId}/allowance`, body);
      await load();
      return true;
    } catch (e) {
      setError(errorText(e, "Could not change that allowance. Please retry."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const users = data?.users || [];
  const anyEditable = users.some((u) => canChange(me, u));
  return (
    <section data-testid="user-spend-table" className="mt-8 rounded-2xl border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="font-heading font-bold">Per-user generation allowance</p>
        <Summary data={data} />
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Secrets: <span className="font-mono2">USER_SPEND_CAP_USD</span> (base, 0 = blocked), <span className="font-mono2">OWNER_ALLOWANCE_USD</span> (owner +
        exempt accounts, a month, max $1000), <span className="font-mono2">RATE_LIMIT_EXEMPT_EMAILS</span> (skip the creation rate limits; their spend has
        the owner allowance), <span className="font-mono2">USER_SPEND_ORDER_CREDIT_USD</span> (per paid duck line, 0 = off),{" "}
        <span className="font-mono2">USER_SPEND_ORDER_CREDIT_ON</span> (placed | shipped), <span className="font-mono2">USER_SPEND_CREDIT_MAX_USD</span> (0 = no maximum).
        Allowance = base + order credits; there is no unlimited, owner included. A maker who would cross it is not charged: the duck waits in a "limit
        reached" state and carries on once an admin raises their allowance (a reason is required and audited) or a print order lands.
      </p>
      {data && users.length > 0 && !anyEditable && (
        <p data-testid="user-spend-view-only" className="mt-2 text-xs font-medium text-muted-foreground">{viewOnlyText()}</p>
      )}
      {!data && !error && (
        <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
      )}
      {data && users.length === 0 && (
        <p data-testid="user-spend-empty" className="mt-3 text-sm text-muted-foreground">No paid calls have been recorded against a signed-in maker yet.</p>
      )}
      {users.length > 0 && (
        <ul className="mt-3 space-y-2">
          {users.map((u) => <Row key={u.user_id} u={u} onAdjust={adjust} busy={busy} editable={canChange(me, u)} />)}
        </ul>
      )}
      {(data?.unmatched_orders || []).length > 0 && (
        <div className="mt-4" data-testid="user-spend-unmatched">
          <p className="text-sm font-semibold">Unmatched orders (duck has no signed-in owner — nobody was credited)</p>
          <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground font-mono2">
            {data.unmatched_orders.map((o) => (
              <li key={`${o.order_name}-${o.duck_id}`} data-testid={`user-spend-unmatched-${o.duck_id}`}>
                {o.order_name} · {o.duck_name || o.duck_id} · buyer "{o.customer_name || "?"}" · {when(o.created_at)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <StaffSpendList staff={data?.staff} />
      <CreditBackfill enabled={!!(data?.credit_settings && data.credit_settings.order_credit_usd > 0)} onApplied={load} />
      {error && <p data-testid="user-spend-error" className="mt-3 text-sm text-rose-700">{error}</p>}
    </section>
  );
};
